<template>
  <div class="installment-list">
    <h1 class="listing-titles">
      {{ $t('InstallmentsList-Title') }}
    </h1>
    <ul>
      <li 
        v-for="(installment, index) of installmentsAlwaysAvailable" 
        :key="index"
      >
        <SuperPacmanInstallmentItem
          :installment="installment"
          @select-installment-item="handleSelectInstallment"
          @other-amount="handleOtherAmount"
        />
      </li> 
    </ul>
    <button 
      v-if="!hideMoreOptions"
      class="installment-list-toggle-button" 
      @click="toggleConditionalInstallments"
    >
      {{ $t('superpacman-more-options') }}
    </button>
    <ul v-if="showConditionalInstallments && !hideMoreOptions">
      <li 
        v-for="(installment, index) of installmentsConditional" 
        :key="index"
      >
        <SuperPacmanInstallmentItem
          :installment="installment"
          @select-installment-item="handleSelectInstallment"
          @other-amount="handleOtherAmount"
        />
      </li> 
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed } from 'vue'
import {ISuperPacmanInstallment, ISuperPacmanInstallmentItem} from "@/types/ISuperPacman"
import SuperPacmanInstallmentItem from "@/components/SuperPacmanInstallmentItem.vue";
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: "SuperPacmanInstallmentList",
  components: {
    SuperPacmanInstallmentItem
  },
  props: {
    installmentDetails: {
      type: Object as PropType<ISuperPacmanInstallment>,
      required: true
    },
  },
  emits: ["select-installment-item", "other-amount"],
  setup(props) {
    const {t} = useI18n();
    const installmentsAlwaysAvailable: ISuperPacmanInstallmentItem[] = []
    const installmentsConditional: ISuperPacmanInstallmentItem[] = []
    const hideMoreOptions = computed(() => {
      if (props.installmentDetails.cumulativeBalance.enabled === true) {
        return true
      } else {
        return false
      }
    })
    const showConditionalInstallments = ref(false);
    return {
      t,
      installmentsAlwaysAvailable,
      installmentsConditional,
      showConditionalInstallments,
      hideMoreOptions
    }     
  },
  created() {
    this.installmentsAlwaysAvailable = this.returnInstallmentsAlwaysAvailable(this.$props.installmentDetails, this.t)
    if (!this.$props.installmentDetails.cumulativeBalance.enabled) {
      this.installmentsConditional = this.returnConditionalInstallments(this.$props.installmentDetails, this.t);
    }
  },
  methods: {
    toggleConditionalInstallments() {
      this.showConditionalInstallments = !this.showConditionalInstallments;
    },
    handleSelectInstallment(selectedInstallmentItem: ISuperPacmanInstallmentItem) {
      this.$emit("select-installment-item", selectedInstallmentItem);
    },
    handleOtherAmount() {
      this.$emit("other-amount");
    },
    translateDaysPaidToLabel(daysPaid: number, isMinimumPayment: boolean, t: (key: string) => string): string[] {
      let label = "";
      let originalLabel = "";
      const minimumPaymentLabel = t("superpacman-minimum-payment");
      /*
        Using if instead of switch because if statements are faster and less costly
      */
      if (daysPaid < 7) {
        originalLabel = "week"
        let dateLabel = t("days");
        if (daysPaid == 1) {
          dateLabel = t("day");
        }
        label = `${daysPaid} ${dateLabel}`;
      } else if (daysPaid === 7) {
        originalLabel = "week"
        label = `${t("week")}`
      } else if (daysPaid === 14) {
        originalLabel = "week2"
        label = `${t("week2")}`
      } else if (daysPaid === 21) {
        originalLabel = "week3"
        label = `${t("week3")}`
      } else if (daysPaid  >= 28)  {
        originalLabel = "month"
        label = `${t("month")}`
      }

      if (isMinimumPayment) {
        label = label.concat(` (${minimumPaymentLabel})`)
      }
      return [originalLabel, label]
    },
    returnInstallmentsAlwaysAvailable(installmentDetails: ISuperPacmanInstallment, t: (key: string) => string): ISuperPacmanInstallmentItem[] {
      const installmentsAlwaysAvailable: ISuperPacmanInstallmentItem[] = [];

      if (installmentDetails.daysToAmount.length > 0) {
        const [minimumAmountOriginalLabel, minimumAmountLabel] = this.translateDaysPaidToLabel(installmentDetails.daysToAmount[0].daysPaid, true, t)
        // We have talked with Corey, Sara and Ricky and we know that the first entry in the daysToAmount list is always going to be the minimum payment
        const minimumPayment: ISuperPacmanInstallmentItem = {
          label: minimumAmountLabel as string,
          amount: installmentDetails.daysToAmount[0].amount,
          currency: installmentDetails.financeProduct.currency,
          originalLabel: minimumAmountOriginalLabel as string
        }
        const remainingBalance: ISuperPacmanInstallmentItem = {
          originalLabel: "superpacman-remaining-balance" as string,
          label: t("superpacman-remaining-balance"),
          amount: installmentDetails.financeProduct.fullPayoffAmount,
          currency: installmentDetails.financeProduct.currency
        }
        /*
          Amount must be equal to "0". This is used in SuperPacmanInstallmentItem.
        */
        const otherAmount: ISuperPacmanInstallmentItem = {
          originalLabel: "superpacman-other-amount",
          label: t("superpacman-other-amount"),
          amount: "0",
          currency: installmentDetails.financeProduct.currency,
        }
        installmentsAlwaysAvailable.push(minimumPayment);
        installmentsAlwaysAvailable.push(remainingBalance);
        if (installmentDetails.cumulativeBalance.enabled === true) {
          installmentsAlwaysAvailable.push(otherAmount)
        }
      }

      return installmentsAlwaysAvailable
    },
    returnConditionalInstallments(installmentDetails: ISuperPacmanInstallment, t: (key: string) => string): ISuperPacmanInstallmentItem[] {
      const conditionalInstallments: ISuperPacmanInstallmentItem[] = [];

      /*
        Amount must be equal to "0". This is used in SuperPacmanInstallmentItem.
      */
      const otherAmount: ISuperPacmanInstallmentItem = {
        originalLabel: "superpacman-other-amount",
        label: t("superpacman-other-amount"),
        amount: "0",
        currency: installmentDetails.financeProduct.currency,
      }

      const weeklyPayments: ISuperPacmanInstallmentItem[] = [];

      installmentDetails.daysToAmount.forEach((value, index) => {
        if (index == 0) {
          // We skip the first index because it's the minimum amount and we have already processed it in the returnInstallmentsAlwaysAvailable function.
        } else {

          let label = "";
          let originalLabel = "";
          if (value.daysPaid >= 30) {
            // This could potentially not work for February
            label = t("month")
            originalLabel = "month"
          } else {
            label = t(DaysToString[value.daysPaid])
            originalLabel = DaysToString[value.daysPaid]
          }
          
          weeklyPayments.push(
            {
              originalLabel: originalLabel,
              label: label,
              amount: value.amount,
              currency: installmentDetails.financeProduct.currency
            } as ISuperPacmanInstallmentItem
          )
        }
      })

      const creditLinePayoffAmount = Number(installmentDetails.financeProduct.creditLinePayoffAmount)
      if (["FEv2", "JointLoanLine"].includes(installmentDetails.financeProduct.financeProductType) && creditLinePayoffAmount > 0) {
        conditionalInstallments.push(otherAmount)
      }

      if (["FEv1", "JointLoanLine"].includes(installmentDetails.financeProduct.financeProductType)) {
        conditionalInstallments.push(...weeklyPayments)
      }

      return conditionalInstallments;
    }
  }
})

enum DaysToString {
  week = 7,
  week2 = 14,
  week3 = 21,
  week4 = 28,
  month = 30
}

</script>

<style scoped>
.installment-list {
  padding: 16px 22px 150px 22px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.installment-list ul {
  list-style: none;
  padding: 0;
}

.animation-container {
  justify-content: center;
  align-items: center;
  flex: 1;
  display: flex;
}

.animation-container h4 {
  color: var(--Typography-Text-1);
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
  margin: auto;
}

.loading-animation {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: auto;
}

.installment-list-toggle-button {
  border: none;
  background: none;
  text-align: right;
  color: #007C3B;
}
</style>
